import { useState } from "react";
import { APP_VERSION } from "@/lib/version";

/* ============================================================
   Falha ao publicar, fixa na tela

   Não é toast. Toast some antes de dar tempo de ler, e num celular não dá
   para abrir o console — sem isso, uma falha que só acontece com uma
   pessoa é impossível de diagnosticar à distância.

   O QUE FICA NA TELA E O QUE FICA NO BOTÃO. Quem está olhando é quem
   tentou publicar um prato, não quem mantém o app: a tela diz o que
   aconteceu e o que dá para fazer, em português. O código curto fica
   junto porque é o que torna um print útil no suporte.

   A mensagem crua do Firebase — em inglês, citando bucket, índice e
   regra — sai da tela e vai só para o botão de copiar. Continua inteira
   para quem for consertar, sem virar parede de jargão para quem não vai.
   ============================================================ */

/* Uma linha por código, dizendo a CAUSA de quem sofreu e, quando existe,
   a saída. Nada de nome de serviço nosso: "Firestore", "Storage",
   "bucket" e "índice" não querem dizer nada para quem só quer postar a
   janta, e ainda assustam. */
const DICAS: Record<string, string> = {
  // Firestore
  "permission-denied": "Você não tem permissão pra isso. Se acabou de entrar no desafio, sai e entra de novo.",
  "unavailable": "Sem conexão agora. Confere a internet e tenta de novo.",
  "invalid-argument": "Algum campo não foi aceito. Confere o que você preencheu e tenta de novo.",
  "resource-exhausted": "O app bateu no limite de uso por agora. Tenta de novo daqui a pouco.",
  "failed-precondition": "Não deu pra completar agora. Tenta de novo daqui a pouco.",
  "deadline-exceeded": "Demorou demais. Costuma ser foto pesada ou internet lenta.",
  // Storage
  "storage/unauthorized": "O envio da foto não foi autorizado. Sai e entra de novo.",
  "storage/unauthenticated": "Sua sessão expirou. Sai e entra de novo.",
  "storage/retry-limit-exceeded": "A foto não subiu depois de várias tentativas. Costuma ser internet instável.",
  "storage/quota-exceeded": "O app bateu no limite de armazenamento. Tenta de novo mais tarde.",
  "storage/unknown": "A foto não subiu. Tenta de novo daqui a pouco — o prato não foi perdido.",
  "nunca-comecou": "O envio não saiu do lugar. Confere a internet e tenta de novo.",
  "parou-no-meio": "O envio parou no meio. Costuma ser internet instável.",
  /* Foto. Estes dois casam pelo prefixo, porque o resto do código diz
     QUAL leitura ou decodificação falhou (`leitura/NotReadableError`,
     `decode/HEIC`) — detalhe que só serve pra quem conserta. */
  "leitura": "Não consegui ler essa foto. Se ela veio de outro app ou ainda está baixando na galeria, tenta de novo; ou tira a foto aqui pelo app.",
  "decode": "Não consegui abrir essa foto. Tenta cortar a foto na galeria, ou tirar a foto aqui pelo app.",
};

export interface Falha { codigo: string; recado: string; etapa: string }

/** Extrai código, mensagem e etapa de um erro qualquer. */
export function descreverFalha(err: unknown, etapa: string): Falha {
  const e = err as { code?: string; codigo?: string; name?: string; message?: string };
  return {
    codigo: e?.codigo ?? e?.code ?? e?.name ?? "sem-código",
    recado: e?.message ?? String(err),
    etapa,
  };
}

export function FalhaCard({ falha }: { falha: Falha }) {
  const [copiado, setCopiado] = useState(false);
  const dica = DICAS[falha.codigo]
    ?? DICAS[falha.codigo.replace(/^firestore\//, "")]
    // Códigos de foto vêm com o detalhe técnico depois da barra.
    ?? DICAS[falha.codigo.split("/")[0] ?? ""]
    ?? "";
  const texto = `Nhomi ${APP_VERSION}\netapa: ${falha.etapa}\ncódigo: ${falha.codigo}\n${falha.recado}`;

  return (
    <div className="card falha-card" role="alert">
      <div className="falha-titulo">Não deu pra publicar</div>
      {dica && <div className="falha-dica">{dica}</div>}
      <div className="falha-codigo">
        {falha.codigo}{falha.etapa ? ` · ao ${falha.etapa}` : ""}
      </div>
      <button
        className="btn btn-white"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(texto);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
          } catch {
            setCopiado(false);
          }
        }}
      >
        {copiado ? "Copiado ✓" : "Copiar detalhes do erro"}
      </button>
    </div>
  );
}
